import { ParsedUrlQuery } from 'querystring';
import camelCase from 'lodash/camelCase';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import sortBy from 'lodash/sortBy';

import { convertToArray, extractFirstValue } from 'shared/utils';
import {
  getPromotionByType,
  getSavingPriceInPromotions,
  isProductTypeInPromotions,
  PromotionType,
} from 'shared/utils/promotion.utils';
import {
  FilterIds,
  Filters,
  GalleryFilters,
  GalleryProducts,
  SortByValue,
  SortOption,
} from '../typings';
import {
  COMPATIBLE_SIM_DISPLAY_LABEL_MAP,
  NETWORK_CAPABILITY_LABEL_MAP,
} from './displayLabelMapping';
import { getAvailableFilterValues } from './getAvailableFilterValues';

export const SortOptions: SortOption[] = [
  { value: 'featured', label: 'Featured first' },
  { value: 'deals-first', label: 'Deals first' },
  { value: 'price-asc', label: 'Price low to high' },
  { value: 'price-desc', label: 'Price high to low' },
  { value: 'date', label: 'Newest' },
];

export const FALLBACK_SORT_VALUE = 'featured';

export const PRICE_RANGE_STEP = 200;

export function buildFilters(query: ParsedUrlQuery): Filters {
  const selectedBrandValues = convertToArray(query.brand);
  const selectedPrimaryColorValues = convertToArray(query.primaryColor);
  const selectedNetworkCapabilityValues = convertToArray(
    query.networkCapability,
  );
  const selectedCompatibleSimValues = convertToArray(query.compatibleSim);

  const price = parseInt(extractFirstValue(query.price), 10) ?? null;

  return {
    primaryColor: selectedPrimaryColorValues,
    brand: selectedBrandValues,
    networkCapability: selectedNetworkCapabilityValues,
    compatibleSim: selectedCompatibleSimValues,
    price,
  };
}

export function parseSortValue(query: ParsedUrlQuery): SortByValue {
  const sort = extractFirstValue(query.sort);

  const matchingSortOption = SortOptions.find(
    (sortOption) => sortOption.value === sort,
  );
  if (matchingSortOption) {
    return matchingSortOption.value;
  }

  return FALLBACK_SORT_VALUE;
}

type FilterProductsParameter = {
  filters: Filters;
  products: GalleryProducts;
};

export const filterProducts = ({
  filters,
  products,
}: FilterProductsParameter) => {
  const { brand, primaryColor, price, compatibleSim, networkCapability } =
    filters;

  return products.filter((product) => {
    return (
      (isEmpty(brand) || brand.includes(camelCase(product.brand))) &&
      (isEmpty(primaryColor) ||
        product.colors?.some((color) =>
          primaryColor.includes(color.primaryColor),
        )) &&
      (isEmpty(compatibleSim) ||
        compatibleSim.includes(camelCase(product.simType))) &&
      (isEmpty(networkCapability) ||
        networkCapability.includes(camelCase(product.band))) &&
      (!price || product.startPricePoint <= price)
    );
  });
};

type SortProductsParameter = {
  products: GalleryProducts;
  sortOrder: SortByValue;
};

function hasHotDealProduct(products: GalleryProducts) {
  return products.some((product) => {
    const originalRRP = getPromotionByType(
      product.promotions,
      PromotionType.ORIGINAL_RRP,
    );
    return (
      product.promotions?.length > 0 ||
      product.pricePointCreditDescription ||
      +originalRRP?.name > 0
    );
  });
}

function sortHotDealProduct(products: GalleryProducts) {
  return sortBy(products, [
    (product) => (product?.stockStatus !== 'OUT_OF_STOCK' ? -1 : 1),
    (product) =>
      isProductTypeInPromotions(product, PromotionType.DEAL) ? -1 : 1,
    (product) =>
      isProductTypeInPromotions(product, PromotionType.MARKETING) ? -1 : 1,
    (product) => -getSavingPriceInPromotions(product),
    (product) => (product?.isFeatured ? -1 : 1),
    (product) => -product?.startPricePoint,
  ]);
}

function sortProducts({ products, sortOrder }: SortProductsParameter) {
  if (sortOrder === 'price-asc') {
    return sortBy(products, 'startPricePoint');
  }
  if (sortOrder === 'price-desc') {
    return sortBy(products, 'startPricePoint').reverse();
  }
  if (sortOrder === 'date') {
    return sortBy(products, 'launchDate').reverse();
  }
  if (sortOrder === 'deals-first') {
    if (hasHotDealProduct(products)) {
      return sortHotDealProduct(products);
    }
  }
  return sortBy(products, [
    (product) => (product?.stockStatus !== 'OUT_OF_STOCK' ? -1 : 1),
    (product) => (product.isFeatured ? -1 : 1),
    (product) => -product.startPricePoint,
  ]);
}

type ApplyFiltersParameter = {
  products: GalleryProducts;
  query: ParsedUrlQuery;
};

export function applyFilters({ products, query }: ApplyFiltersParameter) {
  const filters = buildFilters(query);
  const filteredProducts = filterProducts({ filters, products });
  const sortOrder = parseSortValue(query);

  return sortProducts({ products: filteredProducts, sortOrder });
}

export const getAvailableColorOptions = (
  filters: GalleryFilters,
  products: GalleryProducts,
  query: ParsedUrlQuery,
) => {
  const colorOptions = filters.find(
    (filter) => filter.filterId === FilterIds.PrimaryColor,
  )?.options;

  if (!colorOptions) {
    return null;
  }

  const { brand, price, compatibleSim, networkCapability } = query;

  const filteredProducts = applyFilters({
    products,
    query: { brand, price, compatibleSim, networkCapability },
  });

  const availableProductColors = filteredProducts
    .flatMap((product) => product.colors)
    .filter((color) => color?.primaryColor)
    .map((color) => color.primaryColor);

  return colorOptions
    .filter(({ optionId }) => availableProductColors.includes(optionId))
    .map(({ optionId, label }) => ({
      value: optionId,
      label: `${label} (${
        availableProductColors.filter((color) => color === optionId).length
      })`,
    }));
};

export const getAvailableBrandOptions = (
  filters: GalleryFilters,
  products: GalleryProducts,
  query: ParsedUrlQuery,
) => {
  const brandOptions = filters.find(
    (filter) => filter.filterId === FilterIds.Brand,
  )?.options;

  if (!brandOptions) {
    return null;
  }

  const { primaryColor, price, compatibleSim, networkCapability } = query;
  const filteredProducts = applyFilters({
    products,
    query: { primaryColor, price, compatibleSim, networkCapability },
  });

  const availableProductBrands = filteredProducts.map(
    (product) => product.brand,
  );

  return brandOptions
    .filter((option) => availableProductBrands.includes(option.label))
    .map(({ optionId, label }) => ({
      value: optionId,
      label: `${label} (${
        availableProductBrands.filter((brand) => brand === label).length
      })`,
    }));
};

export const getAvailableCompatibleSimOptions = (
  filters: GalleryFilters,
  products: GalleryProducts,
  query: ParsedUrlQuery,
) => {
  const compatibleSimOptions = filters.find(
    (filter) => filter.filterId === FilterIds.CompatibleSim,
  )?.options;

  if (!compatibleSimOptions) {
    return null;
  }

  const { primaryColor, price, networkCapability, brand } = query;
  const filteredProducts = applyFilters({
    products,
    query: { primaryColor, price, networkCapability, brand },
  });

  const availableProductCompatibleSims = filteredProducts.map(
    (product) => product.simType,
  );

  return compatibleSimOptions
    .filter(
      (option) =>
        Boolean(COMPATIBLE_SIM_DISPLAY_LABEL_MAP[option.label]) &&
        availableProductCompatibleSims.includes(option.label),
    )
    .map(({ optionId, label }) => ({
      value: optionId,
      label: `${COMPATIBLE_SIM_DISPLAY_LABEL_MAP[label]} (${
        availableProductCompatibleSims.filter(
          (compatibleSim) => compatibleSim === label,
        ).length
      })`,
    }));
};

export const getAvailableNetworkCapabilityOptions = (
  filters: GalleryFilters,
  products: GalleryProducts,
  query: ParsedUrlQuery,
) => {
  const networkCapabilityOptions = filters.find(
    (filter) => filter.filterId === FilterIds.NetworkCapability,
  )?.options;

  if (!networkCapabilityOptions) {
    return null;
  }

  const { primaryColor, price, compatibleSim, brand } = query;
  const filteredProducts = applyFilters({
    products,
    query: { primaryColor, price, compatibleSim, brand },
  });

  const availableNetworkCapabilities = filteredProducts.map(
    (product) => product.band,
  );

  return networkCapabilityOptions
    .filter(
      (option) =>
        Boolean(NETWORK_CAPABILITY_LABEL_MAP[option.label]) &&
        availableNetworkCapabilities.includes(option.label),
    )
    .map(({ optionId, label }) => ({
      value: optionId,
      label: `${NETWORK_CAPABILITY_LABEL_MAP[label]} (${
        availableNetworkCapabilities.filter(
          (networkCapability) => networkCapability === label,
        ).length
      })`,
    }));
};

export const generatePriceOptions = (
  products: GalleryProducts,
  step: number = PRICE_RANGE_STEP,
) => {
  const highestProductPrice = Math.max(
    ...products.map((product) => product.startPricePoint),
  );

  const maxPriceOption = Math.ceil(highestProductPrice / step) * step;

  return Array.from({ length: maxPriceOption / step + 1 }, (_, i) => i * step);
};

export const getAvailablePriceOptions = (
  products: GalleryProducts,
  query: ParsedUrlQuery,
): number[] => {
  const priceOptions = generatePriceOptions(products);

  if (!priceOptions) {
    return null;
  }

  const { primaryColor, brand, compatibleSim, networkCapability } = query;
  const filteredProducts = applyFilters({
    products,
    query: { primaryColor, brand, compatibleSim, networkCapability },
  });

  const highestProductPrice = Math.max(
    ...filteredProducts.map((product) => product.startPricePoint),
  );

  return priceOptions.filter(
    (option) => option < highestProductPrice + PRICE_RANGE_STEP,
  );
};

export const removeUnavailableFilterOptions = (
  filters: GalleryFilters,
  products: GalleryProducts,
  newQuery: ParsedUrlQuery,
): ParsedUrlQuery => {
  const availableBrandFilters = getAvailableFilterValues(
    getAvailableBrandOptions,
    filters,
    products,
    newQuery,
  );

  const availableColorFilters = getAvailableFilterValues(
    getAvailableColorOptions,
    filters,
    products,
    newQuery,
  );

  const availableCompatibleSimFilters = getAvailableFilterValues(
    getAvailableCompatibleSimOptions,
    filters,
    products,
    newQuery,
  );

  const availableNetworkCapabilityFilters = getAvailableFilterValues(
    getAvailableNetworkCapabilityOptions,
    filters,
    products,
    newQuery,
  );

  const availablePriceFilters =
    getAvailablePriceOptions(products, newQuery) || [];

  const newBrandFilters = intersection(
    availableBrandFilters,
    convertToArray(newQuery.brand),
  );

  const newNetworkCapabilityFilters = intersection(
    availableNetworkCapabilityFilters,
    convertToArray(newQuery.networkCapability),
  );

  const newCompatibleSimFilters = intersection(
    availableCompatibleSimFilters,
    convertToArray(newQuery.compatibleSim),
  );

  const newColorFilters = intersection(
    availableColorFilters,
    convertToArray(newQuery.primaryColor),
  );

  const highestPriceOption =
    availablePriceFilters[availablePriceFilters.length - 1];

  const sanitisedFilterQuery = pickBy(
    {
      ...newQuery,
      brand: newBrandFilters,
      primaryColor: newColorFilters,
    },
    (filterOption) => !isEmpty(filterOption),
  );

  if (
    isEmpty(newBrandFilters) &&
    isEmpty(newColorFilters) &&
    isEmpty(newNetworkCapabilityFilters) &&
    isEmpty(newCompatibleSimFilters) &&
    highestPriceOption < Number(newQuery.price)
  ) {
    return pick(newQuery, ['category', FilterIds.Sort]);
  }
  if (Number(newQuery.price) > highestPriceOption) {
    return omit(sanitisedFilterQuery, [FilterIds.Price]);
  }

  return sanitisedFilterQuery;
};
